import React from 'react';
import { TrendingUp, Users, Globe, Star, ArrowRight, ExternalLink, Mail, Building2, Target, BarChart3, Zap, Rocket } from 'lucide-react';

const InvestorsPage = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-6">💎</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-white">Invest in the Future of</span>
            <span className="bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent"> AI-Powered Web3</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            AI-powered platform for creating tokenized businesses, complete applications, memecoins and smart contracts. Build with AI, Launch tokens, Deploy globally.
          </p>
        </div>

        {/* Market Opportunity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-400 mr-3" />
              Market Opportunity
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">$67B+ Web3 Market Cap</h3>
                <p className="text-gray-300">
                  The Web3 market is growing rapidly with billions in market capitalization. TokenOS is positioned 
                  to capture significant market share with its innovative AI-powered development platform.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">95% Technical Barrier Problem</h3>
                <p className="text-gray-300">
                  95% of ideas never get built due to technical barriers. TokenOS eliminates these barriers 
                  by enabling anyone to build Web3 applications using natural language.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">$24B AI Development Tools Market</h3>
                <p className="text-gray-300">
                  AI development tools market projected to reach $24B by 2025. TokenOS combines AI with 
                  Web3 development, creating a unique position in this rapidly growing market.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Target className="h-6 w-6 text-purple-400 mr-2" />
                First-Mover Advantage
              </h3>
              <p className="text-gray-300">
                TokenOS has established a first-mover advantage in AI-powered Web3 development, 
                combining cutting-edge AI technology with blockchain infrastructure.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <BarChart3 className="h-6 w-6 text-purple-400 mr-2" />
                Investment Highlights
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Proven product-market fit with growing user base</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Experienced team with deep AI and blockchain expertise</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Scalable technology built for rapid growth</span>
                </div>
                <div className="flex items-center space-x-3">
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300">Multiple revenue streams and diversified monetization</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Model */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <BarChart3 className="h-8 w-8 text-purple-400 mr-3" />
            Revenue Model
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Credit System</h3>
              <p className="text-gray-300 text-sm mb-4">
                Pay-per-use model with tiered pricing for different features. Users purchase credits 
                to access AI development capabilities.
              </p>
              <div className="text-purple-400 font-medium">Primary Revenue Stream</div>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Enterprise Subscriptions</h3>
              <p className="text-gray-300 text-sm mb-4">
                White-label solutions for businesses and development agencies requiring advanced 
                features and custom integrations.
              </p>
              <div className="text-blue-400 font-medium">Growth Revenue Stream</div>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Token Revenue Sharing</h3>
              <p className="text-gray-300 text-sm mb-4">
                50% of net revenue distributed to $TOS token holders monthly, creating 
                sustainable value for the community.
              </p>
              <div className="text-green-400 font-medium">Community Value Stream</div>
            </div>
          </div>
        </div>

        {/* Technology Advantages */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Zap className="h-8 w-8 text-purple-400 mr-3" />
            Technology Advantages
          </h2>
          
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white">Scalable Technology</h3>
                <p className="text-gray-300">
                  Cloud-native architecture built for rapid scaling and enterprise adoption. 
                  Our infrastructure can handle thousands of concurrent users and projects.
                </p>
                
                <h3 className="text-xl font-semibold text-white mt-6">AI Innovation</h3>
                <p className="text-gray-300">
                  Proprietary AI models trained specifically for Web3 development, enabling 
                  natural language to production-ready code generation.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white">Multiple Revenue Streams</h3>
                <p className="text-gray-300">
                  Diversified monetization through credits, subscriptions, and platform fees, 
                  reducing dependency on any single revenue source.
                </p>
                
                <h3 className="text-xl font-semibold text-white mt-6">Experienced Team</h3>
                <p className="text-gray-300">
                  Founded by successful entrepreneurs with deep AI and blockchain expertise, 
                  backed by experienced advisors and industry leaders.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">Growing</div>
            <div className="text-gray-300">User Base</div>
            <div className="text-xs text-gray-500 mt-1">Organic Growth</div>
          </div>
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">Positive</div>
            <div className="text-gray-300">Feedback</div>
            <div className="text-xs text-gray-500 mt-1">Product-Market Fit</div>
          </div>
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">Multiple</div>
            <div className="text-gray-300">Revenue Streams</div>
            <div className="text-xs text-gray-500 mt-1">Diversified Income</div>
          </div>
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">Enterprise</div>
            <div className="text-gray-300">Ready</div>
            <div className="text-xs text-gray-500 mt-1">Scalable Architecture</div>
          </div>
        </div>

        {/* Investment Process */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
            <Rocket className="h-8 w-8 text-purple-400 mr-3" />
            Investment Opportunity
          </h2>
          
          <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Why Invest in TokenOS?</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <ArrowRight className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-300">First-mover in AI-powered Web3 development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <ArrowRight className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-300">Massive addressable market ($67B+ Web3 ecosystem)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <ArrowRight className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-300">Proven technology with growing user adoption</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <ArrowRight className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-300">Revenue sharing model benefits all stakeholders</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Investment Highlights</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Market Validation</span>
                    <span className="text-green-400">✓ Proven</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Technology Moat</span>
                    <span className="text-green-400">✓ Strong</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Team Experience</span>
                    <span className="text-green-400">✓ Deep Expertise</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Revenue Model</span>
                    <span className="text-green-400">✓ Diversified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Learn More?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact our team to learn more about investment opportunities in TokenOS. 
            We're building the future of AI-powered Web3 development.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:investors@tokenos.ai"
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
            >
              <Mail className="h-5 w-5 mr-2" />
              investors@tokenos.ai
            </a>
            <a
              href="https://www.figma.com/deck/1LDjEHduDgUOlwcnSN2Gt2/TokenOS-Pitch-Deck?node-id=1-774&t=6DoCkjd2oeTubnuM-1"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center justify-center"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              View Pitch Deck
            </a>
          </div>
          
          <p className="text-gray-400 text-sm mt-6">
            All investment opportunities are subject to regulatory compliance and accredited investor verification.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvestorsPage;